import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { tap } from 'rxjs';
import { GoogleAd } from 'src/app/core/enums';
import { PlaceAdService } from 'src/app/core/services/place-ad.service';
import { ApiService } from '@shared/services/api.service';
import { AuthService } from '@shared/services/auth.service';
import { CategoriesService } from '@shared/services/categories.service';
import { LocaleService } from '@shared/services/locale.service';
import { ScrollService } from '@shared/services/scroll.service';
import { ViewportService } from '@shared/services/viewport.service';
import { User } from '@core/models';

@Component({
	selector: 'app-home',
	templateUrl: './home.component.html',
	styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private title: Title,
		private auth: AuthService,
		private api: ApiService,
		private categoriesService: CategoriesService,
		private scroll: ScrollService,
		public locale: LocaleService,
		public viewport: ViewportService,
		private placeAd: PlaceAdService,
		private datePipe: DatePipe
	) {}

	loading: boolean = true;
	selectedDate: Date;
	user: User = this.auth.user();
	categories: any[];
	ads: any;
	page: number = 1;
	totalPages: number;
	selectedVal: string;
	maxDate: Date = new Date();
	isAdListIntersecting = false;
	readonly googleAd = GoogleAd;
	@ViewChild('scrollTrigger', { read: ElementRef }) scrollTrigger: ElementRef;
	isMoreAdsLoading: boolean = false;
	private intersectionObserver: IntersectionObserver | undefined;
	private MAX_RETRIES = 10; // Set your maximum retry attempts
	private retryCount = 0;
	params: Params;
	dateforAdsFetched: Date;

	public onValChange(val: string) {
		this.selectedVal = val;
	}

	onDateSelection(date: Date) {
		this.selectedDate = date;
		const formattedDate: string = this.datePipe.transform(date, 'dd-MM-yyyy');

		this.scroll.saveHomeListPageNo(undefined);
		setTimeout(() => {
			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: {
					date: formattedDate,
				},
			});
		}, 0);
	}

	/**
	 * Check if the ancestor element is intersecting to each other
	 *
	 * @param isIntersecting Boolean if two elements are intersecting
	 */
	onAdsPaginationIntersect(isIntersecting: boolean, isAdListIntersectinging: boolean) {
		this.placeAd.setTogglePlaceAdMobile(!isIntersecting && isAdListIntersectinging);
	}

	onGoogleAdBannerIntersect(isIntersecting: boolean) {
		if (this.ads?.['totalPages'] === 1) {
			this.placeAd.setTogglePlaceAdMobile(!isIntersecting && this.isAdListIntersecting);
		}
	}

	fetchAdsOnScroll() {
		if (this.ads?.['totalPages'] > this.page) {
			this.isMoreAdsLoading = true;
			this.params = {
				...this.params,
				page: this.page + 1,
			};

			this.scroll.saveHomeListPageNo(this.params?.page);

			this.fetchAds(this.params?.page, this.params?.page, this.params);
		}
	}

	private initIntersectionObserver(): void {
		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry: IntersectionObserverEntry) => {
					if (entry.isIntersecting) {
						this.fetchAdsOnScroll();
					}
				});
			},
			{ rootMargin: '48px', threshold: 0.1 } // Adjust threshold as needed
		);

		this.intersectionObserver.observe(this?.scrollTrigger?.nativeElement);
	}

	private retryInitIntersectionObserver() {
		if (this.scrollTrigger && this.scrollTrigger.nativeElement instanceof Element) {
			// Trigger intersection observer setup
			this.initIntersectionObserver();
		} else {
			// Retry logic
			if (this.retryCount < this.MAX_RETRIES) {
				this.retryCount++;
				setTimeout(() => this.retryInitIntersectionObserver(), 1000); // Retry after 1 second
			} else {
				console.error(' scrollTrigger is not available.');
			}
		}
	}

	async fetchData(pageNumber: number, parmasData) {
		return new Promise<void>((resolve, reject) => {
			const adsFormData: FormData = new FormData();
			adsFormData.append('page', pageNumber.toString());
			adsFormData.append('date', parmasData['date']);
			adsFormData.append('lang', this.locale.lang);
			if (parmasData?.user) adsFormData.append('userID', parmasData['userID']);
			this.api
				.ads(adsFormData, this.router.url, pageNumber)
				.pipe(
					tap((response: any) => {
						// Show the place ad fixed bottom button if there's no ad list
						this.placeAd.setTogglePlaceAdMobile(!response['response']['data']?.length);
					})
				)
				.subscribe((res) => {
					this.loading = false;

					if (res.status === '1') {
						const newData = res['response']['data'];
						if (this.ads) {
							this.ads.data = this.ads.data.concat(newData);
							this.ads.totalPages = res['response']['pages'];
							this.ads.totalRows = res['response']['totalRows'];
							this.page = this.page + 1;
						} else {
							// If ads is empty or doesn't exist, set it to the new data
							this.ads = {
								data: newData,
								totalPages: res['response']['pages'],
								totalRows: res['response']['totalRows'],
							};
						}

						// this.ads = res['response']['data'];
						// this.totalPages = res['response']['pages'];
						this.isMoreAdsLoading = false;
						resolve(); // Resolve the Promise once API call is completed

						// scroll to stored position
						const _scroll = this.scroll.fetch();

						if (_scroll && _scroll['trigger'] === true) {
							setTimeout(() => {
								window.scrollTo({ left: _scroll['xpos'], top: _scroll['ypos'] });
							}, 0);
						}

						// clear scroll
						this.scroll.save(undefined, undefined, undefined, undefined);
					}
				}),
				(err) => {
					this.loading = false;
					this.isMoreAdsLoading = false;
					reject(err); // Reject the Promise with the error
				};
		});
	}

	async fetchAds(startPage: number, endPage: number, paramsData: any) {
		for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
			await this.fetchData(pageNumber, paramsData);
		}
	}

	ngOnInit(): void {
		this.title.setTitle('Kurd Shopping - Buy and sell anything in Kurdistan Iraq');
		this.selectedVal = 'allCategories';

		// categories
		const categoriesFormData: FormData = new FormData();
		categoriesFormData.append('lang', this.locale.lang);

		this.api.categories(categoriesFormData).subscribe((res) => {
			this.categories = this.categoriesService.strip(res['response']);
		});

		const PAGE_NO = this.scroll.getHomePageNo();

		// selected date (if any)
		const adsFormData: FormData = new FormData();

		this.route.queryParams.subscribe((params: Params) => {
			// get current page
			if (params['page']) {
				this.page = +params['page'];
			}

			if (params['date'] === undefined) {
				this.selectedDate = new Date();

				adsFormData.append('date', this.datePipe.transform(this.selectedDate, 'dd-MM-yyyy'));
			} else {
				const dateParts: string[] = params['date'].split('-');
				const year: number = +dateParts[2];
				const month: number = +dateParts[1] - 1;
				const day: number = +dateParts[0];
				this.selectedDate = new Date(year, month, day);
				adsFormData.append('date', params['date']);
			}
			this.params = {
				...params,
				page: this.page,
				date: adsFormData.get('date'),
			};

			this.params = {
				...this.params,
				lang: this.locale.lang,
				...(this.user ? { userID: this.user['userID'] } : {}),
			};

			if (PAGE_NO) {
				this.fetchAds(1, PAGE_NO, this.params);
			} else {
				this.fetchAds(1, 1, this.params);
			}
		});
	}

	ngAfterViewInit() {
		//somehow scrollTrigger Element is not getting renedered intially in ngAfterViewInit so used this logic of retry
		this.retryInitIntersectionObserver();
	}

	ngOnDestroy(): void {
		this.placeAd.setTogglePlaceAdMobile(false);
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
		}
	}
}
